import { ORDER_STATUS, ORDER_STATUS_REASON, type Order, type OrderStatusReason } from '@klareospace/pharos-lib-types';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Which leg of an order (pickup or delivery) an address-related anomaly applies to. */
export const ADDRESS_ANOMALY_SIDE = { DELIVERY: 'delivery', PICKUP: 'pickup' } as const;
export type AddressAnomalySide = (typeof ADDRESS_ANOMALY_SIDE)[keyof typeof ADDRESS_ANOMALY_SIDE];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns true when the order is in ANOMALY status and its statusReason
 * matches the given reason code.
 */
export function hasAnomalyReason(order: Order, reason: OrderStatusReason): boolean {
  return order.status === ORDER_STATUS.ANOMALY && order.statusReason === reason;
}

/**
 * Resolves which leg of the order (pickup or delivery) an address-related anomaly
 * applies to, so the UI can render the error banner alongside the address block
 * that actually failed rather than at a fixed location.
 *
 * Geocoding today only ever runs against the delivery address — pickup is always
 * a resolved site — so GEOCODING_FAILED always maps to 'delivery'. Returns
 * undefined when the order has no address-related anomaly.
 */
export function resolveAddressAnomalySide(order: Order): AddressAnomalySide | undefined {
  if (hasAnomalyReason(order, ORDER_STATUS_REASON.GEOCODING_FAILED)) {
    return ADDRESS_ANOMALY_SIDE.DELIVERY;
  }
  return undefined;
}
